import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { unwrap } from '@/lib/result';
import { isoDateRange } from '@/shared/lib/dates';
import { useSessionStore } from '@/stores/session.store';

import { coverageRepository } from '../api/coverage.repository';
import type { CoverageRequestCreateInput } from '../model/coverage-request';

export const coverageKeys = {
  all: ['coverage'] as const,
  open: () => [...coverageKeys.all, 'open'] as const,
  mine: (createdBy: string) => [...coverageKeys.all, 'mine', createdBy] as const,
  request: (id: string) => [...coverageKeys.all, 'request', id] as const,
  myApplication: (requestId: string, providerId: string) =>
    [...coverageKeys.all, 'myApplication', requestId, providerId] as const,
  myApplications: (providerId: string) =>
    [...coverageKeys.all, 'myApplications', providerId] as const,
  applications: (requestId: string) => [...coverageKeys.all, 'applications', requestId] as const,
  matched: () => [...coverageKeys.all, 'matched'] as const,
  candidates: (requestId: string) => [...coverageKeys.all, 'candidates', requestId] as const,
};

export function useOpenRequests() {
  return useQuery({
    queryKey: coverageKeys.open(),
    queryFn: () => unwrap(coverageRepository.listOpen()),
  });
}

/** Phase 2: ranked feed of nearby open requests for the signed-in provider. */
export function useMatchedRequests() {
  return useQuery({
    queryKey: coverageKeys.matched(),
    queryFn: () => unwrap(coverageRepository.matchedRequests()),
  });
}

/** Phase 2: ranked candidate providers for a request (owner only). */
export function useCandidates(requestId: string, enabled: boolean) {
  return useQuery({
    queryKey: coverageKeys.candidates(requestId),
    queryFn: () => unwrap(coverageRepository.candidatesForRequest(requestId)),
    enabled,
  });
}

export function useMyRequests() {
  const userId = useSessionStore((s) => s.userId);
  return useQuery({
    queryKey: coverageKeys.mine(userId ?? 'anon'),
    queryFn: () => unwrap(coverageRepository.listByCreator(userId as string)),
    enabled: Boolean(userId),
  });
}

export function useRequest(id: string) {
  return useQuery({
    queryKey: coverageKeys.request(id),
    queryFn: () => unwrap(coverageRepository.getById(id)),
  });
}

export function useCreateRequest() {
  const queryClient = useQueryClient();
  const userId = useSessionStore((s) => s.userId);
  return useMutation({
    mutationFn: (input: Omit<CoverageRequestCreateInput, 'createdBy'>) =>
      unwrap(coverageRepository.create({ ...input, createdBy: userId as string })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: coverageKeys.mine(userId ?? 'anon') });
    },
  });
}

export function useCancelRequest(requestId: string) {
  const queryClient = useQueryClient();
  const userId = useSessionStore((s) => s.userId);
  return useMutation({
    mutationFn: () => unwrap(coverageRepository.cancel(requestId)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: coverageKeys.request(requestId) });
      void queryClient.invalidateQueries({ queryKey: coverageKeys.mine(userId ?? 'anon') });
    },
  });
}

export function useMyApplicationForRequest(requestId: string) {
  const userId = useSessionStore((s) => s.userId);
  return useQuery({
    queryKey: coverageKeys.myApplication(requestId, userId ?? 'anon'),
    queryFn: () => unwrap(coverageRepository.myApplicationForRequest(requestId, userId as string)),
    enabled: Boolean(userId),
  });
}

export function useApply(requestId: string) {
  const queryClient = useQueryClient();
  const userId = useSessionStore((s) => s.userId);
  return useMutation({
    mutationFn: (message: string | null) =>
      unwrap(coverageRepository.apply(requestId, userId as string, message)),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: coverageKeys.myApplication(requestId, userId ?? 'anon'),
      });
      void queryClient.invalidateQueries({ queryKey: coverageKeys.open() });
      void queryClient.invalidateQueries({
        queryKey: coverageKeys.myApplications(userId ?? 'anon'),
      });
    },
  });
}

export function useMyApplications() {
  const userId = useSessionStore((s) => s.userId);
  return useQuery({
    queryKey: coverageKeys.myApplications(userId ?? 'anon'),
    queryFn: () => unwrap(coverageRepository.listMyApplications(userId as string)),
    enabled: Boolean(userId),
  });
}

export function useRequestApplications(requestId: string, enabled: boolean) {
  return useQuery({
    queryKey: coverageKeys.applications(requestId),
    queryFn: () => unwrap(coverageRepository.listApplicationsForRequest(requestId)),
    enabled,
  });
}

export function useAcceptApplication(requestId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (applicationId: string) => unwrap(coverageRepository.accept(applicationId)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: coverageKeys.request(requestId) });
      void queryClient.invalidateQueries({ queryKey: coverageKeys.applications(requestId) });
    },
  });
}

export function useCompleteApplication(requestId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { applicationId: string; outcome: 'completed' | 'no_show' }) =>
      unwrap(coverageRepository.completeApplication(vars.applicationId, vars.outcome)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: coverageKeys.applications(requestId) });
      void queryClient.invalidateQueries({ queryKey: coverageKeys.request(requestId) });
    },
  });
}

export function useRecordAttendance(requestId: string) {
  const queryClient = useQueryClient();
  const userId = useSessionStore((s) => s.userId);
  return useMutation({
    mutationFn: (vars: { applicationId: string; lat: number; lng: number; kind: 'in' | 'out' }) =>
      unwrap(
        coverageRepository.recordAttendance(vars.applicationId, vars.lat, vars.lng, vars.kind),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: coverageKeys.myApplication(requestId, userId ?? 'anon'),
      });
    },
  });
}

/**
 * Fixes "prayer times not synced": fetches + upserts prayer times (AlAdhan) for
 * every date in the request's range. Only the masjid owner/manager may call this
 * (enforced server-side by upsert_prayer_times).
 */
export function useSyncPrayerTimes() {
  return useMutation({
    mutationFn: async (vars: {
      masjidId: string;
      lat: number;
      lng: number;
      startDate: string;
      endDate: string;
    }) => {
      for (const date of isoDateRange(vars.startDate, vars.endDate)) {
        await unwrap(
          coverageRepository.syncPrayerTimesForDate(vars.masjidId, vars.lat, vars.lng, date),
        );
      }
    },
  });
}

/**
 * Auto-triggers useSyncPrayerTimes once per unique (masjid, date range) so an
 * owner viewing their accepted request populates the check-in verification cache
 * without needing a manual action. Best-effort: failures (e.g. offline) are
 * silently ignored — check-in still works, just unverified, exactly as designed
 * for a supplementary signal.
 */
export function useAutoSyncPrayerTimes(
  masjidId: string | null,
  lat: number | null,
  lng: number | null,
  startDate: string,
  endDate: string,
): void {
  const sync = useSyncPrayerTimes();
  const triggeredKey = useRef<string | null>(null);

  useEffect(() => {
    if (!masjidId || lat == null || lng == null) return;
    const key = `${masjidId}:${startDate}:${endDate}`;
    if (triggeredKey.current === key) return;
    triggeredKey.current = key;
    sync.mutate({ masjidId, lat, lng, startDate, endDate });
  }, [masjidId, lat, lng, startDate, endDate, sync]);
}

export function useWithdrawApplication() {
  const queryClient = useQueryClient();
  const userId = useSessionStore((s) => s.userId);
  return useMutation({
    mutationFn: (applicationId: string) => unwrap(coverageRepository.withdraw(applicationId)),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: coverageKeys.myApplications(userId ?? 'anon'),
      });
    },
  });
}
